import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FolderOpen, FileText, Users, Activity as ActivityIcon, UploadCloud } from "lucide-react";
import { Link } from "react-router-dom";
import { useDriveAuth } from "../context/DriveAuthContext";
import { fetchFolders, fetchFiles, fetchActivity, fetchArtists } from "../lib/driveClient";

const StatCard = ({ icon: Icon, label, value, hint }) => (
  <div className="relative bg-[#0A0A0A] border border-white/[0.06] p-6 hover:border-[#ff0033]/30 transition-colors">
    <div className="absolute top-4 right-4 red-led opacity-40" />
    <Icon size={18} className="text-[#ff0033] mb-4" />
    <div className="font-display font-black text-3xl md:text-4xl tracking-tight">{value}</div>
    <div className="mt-1 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">{label}</div>
    {hint && <div className="mt-3 text-xs text-zinc-500">{hint}</div>}
  </div>
);

const ACTION_LABELS = {
  upload_file: "hat Datei hochgeladen",
  download_file: "hat Datei heruntergeladen",
  delete_file: "hat Datei gelöscht",
  create_folder: "hat Ordner erstellt",
  create_artist: "hat Künstler angelegt",
  create_customer: "hat Kunden angelegt",
  login: "hat sich eingeloggt",
};

export default function DriveDashboard() {
  const { profile, isStaff, role } = useDriveAuth();
  const [stats, setStats] = useState({ folders: 0, files: 0, artists: 0 });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [f, fi, act] = await Promise.all([
          fetchFolders().catch(() => []),
          fetchFiles(null).catch(() => []),
          fetchActivity(8).catch(() => []),
        ]);
        let artists = [];
        if (isStaff) artists = await fetchArtists().catch(() => []);
        if (!cancel) {
          setStats({ folders: f.length, files: fi.length, artists: artists.length });
          setActivities(act);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => (cancel = true);
  }, [isStaff]);

  return (
    <div className="space-y-8">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">
          <span className="block w-8 h-px bg-[#ff0033] shadow-[0_0_8px_#ff0033]" />
          Dashboard
        </div>
        <h1 className="font-display font-black uppercase tracking-tighter leading-none text-4xl md:text-5xl">
          Willkommen{profile?.display_name ? `, ${profile.display_name}` : ""}.
        </h1>
        <p className="mt-3 text-sm text-zinc-500">
          {role === "superadmin"
            ? "Volle Kontrolle über alle Bereiche der Plattform."
            : role === "staff"
            ? "Zugriff auf die freigegebenen Bereiche."
            : role === "artist"
            ? "Dein persönlicher Bereich mit Verträgen, Songs und Uploads."
            : "Dein Bereich mit Verträgen, NDA und Dokumenten."}
        </p>
      </motion.div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FolderOpen} label="Ordner" value={loading ? "–" : stats.folders} />
        <StatCard icon={FileText} label="Dateien" value={loading ? "–" : stats.files} />
        {isStaff && <StatCard icon={Users} label="Künstler" value={loading ? "–" : stats.artists} />}
        <StatCard icon={ActivityIcon} label="Events (24h)" value={loading ? "–" : activities.length} />
      </div>

      {/* Quick actions */}
      <div className="grid md:grid-cols-2 gap-4">
        <Link
          to="/drive/files"
          data-testid="quick-files"
          className="group relative bg-[#0A0A0A] border border-white/[0.06] p-6 hover:border-[#ff0033]/40 transition-all hover:-translate-y-0.5"
        >
          <div className="flex items-start justify-between">
            <div>
              <UploadCloud size={22} className="text-[#ff0033] mb-3" />
              <div className="font-display font-black uppercase text-xl">Dateien & Upload</div>
              <div className="text-xs text-zinc-500 mt-2">
                Ordner durchsuchen, Dateien hochladen und verwalten.
              </div>
            </div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600 group-hover:text-white transition-colors">
              öffnen →
            </span>
          </div>
        </Link>

        {isStaff && (
          <Link
            to="/drive/artists"
            data-testid="quick-artists"
            className="group relative bg-[#0A0A0A] border border-white/[0.06] p-6 hover:border-[#ff0033]/40 transition-all hover:-translate-y-0.5"
          >
            <div className="flex items-start justify-between">
              <div>
                <Users size={22} className="text-[#ff0033] mb-3" />
                <div className="font-display font-black uppercase text-xl">Künstler verwalten</div>
                <div className="text-xs text-zinc-500 mt-2">
                  Neuen Künstler anlegen, Daten pflegen, Verträge zuordnen.
                </div>
              </div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600 group-hover:text-white transition-colors">
                öffnen →
              </span>
            </div>
          </Link>
        )}
      </div>

      {/* Recent Activity */}
      <div>
        <div className="flex items-center gap-3 mb-5 text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">
          <ActivityIcon size={12} className="text-[#ff0033]" />
          Letzte Aktivitäten
        </div>
        <div className="bg-[#0A0A0A] border border-white/[0.06] divide-y divide-white/[0.04]">
          {loading ? (
            <div className="p-6 text-sm text-zinc-500">Lade…</div>
          ) : activities.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500">Noch keine Aktivitäten</div>
          ) : (
            activities.map((a) => (
              <div key={a.id} className="p-4 md:px-6 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <span className="red-led shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">
                      <span className="text-zinc-400">{ACTION_LABELS[a.action] || a.action}</span>
                      {a.target_name && <span className="text-white ml-2">{`„${a.target_name}"`}</span>}
                    </div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 mt-1">
                      {new Date(a.created_at).toLocaleString("de-DE")}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
